import { useState, useEffect } from 'react';

/**
 * Fetches candidates for a state and office from the elections API.
 *
 * The endpoint returns one row per filing, so a candidate who has run in
 * several cycles appears several times. Rows are deduplicated by candidate_id
 * with the most recent filing kept - the legacy list rendered the duplicates,
 * which is why the same name showed up two and three times down a state's list.
 *
 * @param {string} stateAbbr - Two letter state code. Omit for President.
 * @param {string} electionType - 'House' | 'Senate' | 'President'
 * @param {number} limit
 * @returns {{ candidates: Array, loading: boolean, error: Error|null }}
 */

const CANDIDATES_ENDPOINT = 'https://api.govglance.org/elections/election_candidates';

const byMostRecentFiling = (a, b) =>
  new Date(b.last_file_date) - new Date(a.last_file_date);

export const dedupeCandidates = (rows) => {
  const byId = new Map();

  [...rows].sort(byMostRecentFiling).forEach((row) => {
    if (!row.candidate_id) return;
    if (!byId.has(row.candidate_id)) {
      byId.set(row.candidate_id, row);
    }
  });

  return [...byId.values()];
};

const useCandidates = (stateAbbr, electionType, limit = 200) => {
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const apiKey = process.env.REACT_APP_API_KEY;

  useEffect(() => {
    if (!electionType) {
      setCandidates([]);
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);

    const fetchCandidates = async () => {
      try {
        // Presidential candidates are not filed under a state, so the filter is
        // left off entirely rather than sent empty.
        const stateParam =
          electionType === 'President' || !stateAbbr ? '' : `state=${stateAbbr}&`;

        const url =
          `${CANDIDATES_ENDPOINT}?${stateParam}` +
          `election_type=${electionType}` +
          `&order_by=load_date&limit=${limit}&skip=0`;

        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${apiKey}`,
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        if (cancelled) return;

        setCandidates(dedupeCandidates(Array.isArray(data) ? data : []));
        setError(null);
      } catch (err) {
        if (cancelled) return;
        console.error('Error fetching candidates:', err);
        setCandidates([]);
        setError(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchCandidates();

    return () => {
      cancelled = true;
    };
  }, [stateAbbr, electionType, limit, apiKey]);

  return { candidates, loading, error };
};

export default useCandidates;